// =============================================================
//  HomeScreenCentre — Dashboard du centre (refonte web)
// =============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, useWindowDimensions, TouchableOpacity } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import { useAuth } from '../private/AuthContext';

import CentreLayout from './web-ui/CentreLayout';
import {
  Card,
  StatCard,
  Button,
  Badge,
  EmptyState,
  Loading,
  DataRow,
} from '../admin/web-ui/kit';
import { colors, spacing } from '../admin/web-ui/theme';

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('fr-FR');
};

const stateVariant = (e) => {
  const v = String(e || '').toLowerCase();
  if (['approved', 'active'].includes(v)) return 'success';
  if (['pending', 'verification', 'en-cours'].includes(v)) return 'warning';
  if (['rejected', 'cancelled', 'expired'].includes(v)) return 'danger';
  return 'neutral';
};

function MobileFallback({ navigation, user }) {
  return (
    <View style={{ flex: 1, padding: 20, backgroundColor: '#f7f9fc' }}>
      <Text style={{ fontSize: 22, fontWeight: '700', color: '#1a2236' }}>
        Bienvenue {user?.centerName || ''}
      </Text>
      <Text style={{ fontSize: 13, color: '#5d6781', marginTop: 6 }}>
        Tableau de bord de votre centre
      </Text>
      <TouchableOpacity
        onPress={() => navigation.navigate('CreateContentScreen')}
        style={{
          marginTop: 20, backgroundColor: '#165ca2', padding: 14, borderRadius: 10,
          flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        <MaterialCommunityIcons name="plus" size={20} color="#fff" />
        <Text style={{ color: '#fff', fontWeight: '700' }}>Publier une annonce</Text>
      </TouchableOpacity>
    </View>
  );
}

export default function HomeScreenCentre({ navigation }) {
  const { user } = useAuth();
  const isWeb = typeof window !== 'undefined' && !!window.document;

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, rejected: 0 });
  const [recent, setRecent] = useState([]);

  const loadData = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      let snap;
      try {
        snap = await getDocs(query(
          collection(db, 'announcements'),
          where('centerInfo.centerId', '==', user.uid),
          orderBy('createdAt', 'desc')
        ));
      } catch {
        snap = await getDocs(query(
          collection(db, 'announcements'),
          where('centerInfo.centerId', '==', user.uid)
        ));
      }
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const lower = (v) => String(v || '').toLowerCase();
      const active = items.filter((a) => ['approved', 'active'].includes(lower(a.etat || a.status))).length;
      const pending = items.filter((a) => ['pending', 'verification', 'en-cours', ''].includes(lower(a.etat || a.status))).length;
      const rejected = items.filter((a) => ['rejected', 'cancelled'].includes(lower(a.etat || a.status))).length;

      setStats({ total: items.length, active, pending, rejected });
      setRecent(items.slice(0, 5));
    } catch (e) {
      console.log('centre home load error', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!isWeb) {
    return <MobileFallback navigation={navigation} user={user} />;
  }

  const centerName = user?.centerName || user?.fullName || 'Centre';

  return (
    <CentreLayout
      title="Tableau de bord"
      subtitle={`Bienvenue ${centerName} — voici l'état de votre activité`}
    >
      <View style={styles.actionsRow}>
        <Button icon="plus-circle-outline" onPress={() => navigation.navigate('CreateContentScreen')}>
          Publier une annonce
        </Button>
        <Button icon="bullhorn-outline" variant="secondary" onPress={() => navigation.navigate('CentreScreenCentre2')}>
          Mes annonces
        </Button>
        <Button icon="history" variant="secondary" onPress={() => navigation.navigate('HistoriqueScreenCentre')}>
          Historique
        </Button>
      </View>

      <View style={styles.statsRow}>
        <StatCard label="Total annonces" value={stats.total} icon="bullhorn-outline" accent={colors.primary} loading={loading} />
        <StatCard label="Approuvées" value={stats.active} icon="check-circle-outline" accent={colors.success} loading={loading} />
        <StatCard label="En attente" value={stats.pending} icon="clock-outline" accent={colors.warning} loading={loading} />
        <StatCard label="Rejetées" value={stats.rejected} icon="close-circle-outline" accent={colors.danger} loading={loading} />
      </View>

      <Card noPadding style={{ marginTop: spacing.lg }}>
        <View style={styles.tableHeader}>
          <View style={{ flex: 1 }}>
            <Text style={styles.tableHeaderTitle}>Annonces récentes</Text>
            <Text style={styles.tableHeaderSub}>Les 5 dernières que vous avez publiées</Text>
          </View>
          <Button size="sm" variant="ghost" icon="arrow-right" onPress={() => navigation.navigate('CentreScreenCentre2')}>
            Voir toutes
          </Button>
        </View>
        {loading ? (
          <Loading />
        ) : recent.length === 0 ? (
          <EmptyState
            icon="bullhorn-outline"
            title="Aucune annonce"
            subtitle="Publie ta première annonce pour la voir apparaître ici"
            action={
              <Button icon="plus-circle-outline" onPress={() => navigation.navigate('CreateContentScreen')}>
                Publier une annonce
              </Button>
            }
          />
        ) : (
          recent.map((a, i) => (
            <DataRow
              key={a.id}
              last={i === recent.length - 1}
              onPress={() => navigation.navigate('AnnouncementDetailScreen', { id: a.id, announcement: a })}
            >
              <View style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: colors.primarySoft, alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ color: colors.primary, fontWeight: '700', fontSize: 16 }}>
                  {(a.titre || '?').charAt(0).toUpperCase()}
                </Text>
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={styles.rowTitle} numberOfLines={1}>{a.titre || 'Sans titre'}</Text>
                <Text style={styles.rowSubtitle} numberOfLines={1}>
                  {fmtDate(a.createdAt)} · {a.concours?.length || 0} concours
                </Text>
              </View>
              <Badge variant={stateVariant(a.etat || a.status)}>
                {a.etat || a.status || 'pending'}
              </Badge>
              <MaterialCommunityIcons name="chevron-right" size={18} color={colors.textMuted} />
            </DataRow>
          ))
        )}
      </Card>
    </CentreLayout>
  );
}

const styles = StyleSheet.create({
  actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.xl },
  statsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.lg, marginBottom: spacing.xl },
  tableHeader: {
    flexDirection: 'row', alignItems: 'center', padding: spacing.lg,
    borderBottomWidth: 1, borderBottomColor: colors.border,
  },
  tableHeaderTitle: { fontSize: 15, fontWeight: '700', color: colors.text },
  tableHeaderSub: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
  rowTitle: { fontSize: 14, fontWeight: '600', color: colors.text },
  rowSubtitle: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
});
